import React from "react";
import { useNavigate } from "react-router";

import { translation } from "@/i18n";
import { nsVersion } from "@/i18n/locales/version";
import { useAppRouteParams } from "../../../../../AppRoutes";
import { postRemoveVersion } from "../../../../../api/version";
import { Version } from "../../../../../domain/Version";
import { useBulkDialog } from "../../../helpers/DialogBulk/hooks";

const useTranslation = translation.use(nsVersion);

export const DialogVersionRemoveSome: React.FC<{
  versions: Version[];
  selectedVersionIds: string[];
  setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  versions,
  selectedVersionIds,
  setSelectedVersionIds,
  open,
  setOpen,
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId: nowVersionId } = useAppRouteParams();
  const navigate = useNavigate();

  const selectedVersions = versions.filter(({ id }) =>
    selectedVersionIds.includes(id)
  );

  const runRemove = async (version: Version) => {
    await postRemoveVersion({
      organizationId,
      versionId: version.id,
    });
    // APIの実行時の制限に従い、1秒待機
    // ref: [レートリミット: どの1秒間(ウィンドウ)についても「ウィンドウのコスト」が6を超えないこと。](https://www.notion.so/algo-artis/SSUI-API-2025-07-07ver-184af951024b807897a6cf64f1f2fb91)
    // 削除においてはAPIを呼ぶたびに1秒待機することで、レートリミットを回避する。
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  const { renderBulkDialogs } = useBulkDialog<"id", Version>({
    title: t.version.removeSomeDialog.title(),
    displayMessage: (
      <>
        <div>{t.version.removeSomeDialog.message()}</div>
        <div style={{ color: "var(--figma-color-status-error)", marginTop: 8 }}>
          {t.version.removeSomeDialog.warning()}
        </div>
      </>
    ),
    startButtonText: t.version.removeSomeDialog.startButton(),
    operationName: t.version.removeSomeDialog.title(),
    rows: selectedVersions,
    rowKey: "id",
    run: runRemove,
    isThemeRed: true,
    renderItem: (version) => {
      return <div>{version.title}</div>;
    },
    open,
    onClose: ({ states }) => {
      setOpen(false);
      const remainingIds = states
        .filter((s) => s.status !== "success")
        .map((s) => s.id);
      setSelectedVersionIds(remainingIds);

      // 完全削除に成功したバージョンに現在表示中のバージョンが含まれていたらナビゲート
      const successIds = states
        .filter((s) => s.status === "success")
        .map((s) => s.id);
      if (successIds.includes(nowVersionId)) {
        navigate(`/organizations/${organizationId}`);
      }
    },
  });

  return <>{renderBulkDialogs()}</>;
};
